import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { ExportButton } from '@/components/ExportButton';
import { toast } from 'sonner';
import { 
  DollarSign, 
  Ticket, 
  Users, 
  TrendingUp,
  Trophy,
  ArrowUpRight,
  ArrowDownRight,
  Mail,
  Loader2,
  Clock,
  CheckCircle
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar
} from 'recharts';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardPage = () => {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  // Iter 67 · default "annual" — evita "sin datos" en pantalla inicial.
  const [period, setPeriod] = useState('annual');
  const [sendingEmails, setSendingEmails] = useState(false);
  const [pasajerosStats, setPasajerosStats] = useState({ pending: 0, completed: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [period]);

  const fetchData = async () => {
    try {
      const headers = getAuthHeaders();
      const [statsRes, chartRes, leaderboardRes, pasajerosRes] = await Promise.all([
        axios.get(`${API_URL}/dashboard/stats?period=${period}`, { headers }),
        axios.get(`${API_URL}/dashboard/chart-data?period=${period}`, { headers }),
        axios.get(`${API_URL}/leaderboard?period=${period}`, { headers }),
        axios.get(`${API_URL}/ventas/grouped-by-status`, { headers })
      ]);
      setStats(statsRes.data);
      setChartData(chartRes.data);
      setLeaderboard(leaderboardRes.data.slice(0, 5));
      setPasajerosStats({
        pending: pasajerosRes.data.total_pending || 0,
        completed: pasajerosRes.data.total_completed || 0
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendReports = async () => {
    setSendingEmails(true);
    try {
      await axios.post(`${API_URL}/email/send-monthly-reports`, {}, {
        headers: getAuthHeaders()
      });
      toast.success('📧 Reportes enviándose a todos los gestores');
    } catch (error) {
      toast.error('Error al enviar reportes');
    } finally {
      setSendingEmails(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(value);
  };

  const kpiCards = stats ? [
    {
      title: 'Ventas Totales',
      value: formatCurrency(stats.total_sales),
      icon: DollarSign,
      color: 'blue',
      testId: 'kpi-total-sales'
    },
    {
      title: 'Pasajes Vendidos',
      value: stats.total_tickets.toLocaleString(),
      icon: Ticket,
      color: 'yellow',
      testId: 'kpi-total-tickets'
    },
    {
      title: 'Gestores Activos',
      value: stats.total_gestores,
      icon: Users,
      color: 'green',
      testId: 'kpi-total-gestores'
    },
    {
      title: 'Crecimiento',
      value: `${stats.sales_growth > 0 ? '+' : ''}${stats.sales_growth}%`,
      icon: TrendingUp,
      color: stats.sales_growth >= 0 ? 'green' : 'red',
      trend: stats.sales_growth,
      testId: 'kpi-growth'
    }
  ] : [];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 rounded-lg shadow-xl border border-slate-200">
          <p className="text-sm font-medium text-slate-900">{label}</p>
          <p className="text-sm text-blue-600">
            Ventas: {formatCurrency(payload[0]?.value || 0)}
          </p>
          <p className="text-sm text-yellow-600">
            Pasajes: {payload[1]?.value || 0}
          </p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-10 h-10"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeIn" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Dashboard
          </h1>
          <p className="text-slate-500 mt-1">Resumen de ventas de Aircom Connect</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Button
            variant="outline"
            onClick={handleSendReports}
            disabled={sendingEmails}
            className="border-blue-200 hover:bg-blue-50"
            data-testid="send-reports-btn"
          >
            {sendingEmails ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Mail className="w-4 h-4 mr-2 text-blue-600" />
            )}
            Enviar Reportes
          </Button>
          <ExportButton type="pdf" />
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-40" data-testid="period-selector">
              <SelectValue placeholder="Período" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="daily">Diario</SelectItem>
              <SelectItem value="weekly">Semanal</SelectItem>
              <SelectItem value="monthly">Mensual</SelectItem>
              <SelectItem value="annual">Anual</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 stagger-children">
        {kpiCards.map((kpi) => (
          <Card 
            key={kpi.title} 
            className="kpi-card card-hover border-l-4"
            style={{ borderLeftColor: kpi.color === 'blue' ? '#0044CC' : kpi.color === 'yellow' ? '#FFCC00' : kpi.color === 'green' ? '#10B981' : '#EF4444' }}
            data-testid={kpi.testId}
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">{kpi.title}</p>
                  <p className="text-2xl font-bold text-slate-900 mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {kpi.value}
                  </p>
                </div>
                <div className={`p-3 rounded-lg ${
                  kpi.color === 'blue' ? 'bg-blue-100' : 
                  kpi.color === 'yellow' ? 'bg-yellow-100' : 
                  kpi.color === 'green' ? 'bg-green-100' : 'bg-red-100'
                }`}>
                  <kpi.icon className={`w-5 h-5 ${
                    kpi.color === 'blue' ? 'text-blue-600' : 
                    kpi.color === 'yellow' ? 'text-yellow-600' : 
                    kpi.color === 'green' ? 'text-green-600' : 'text-red-600'
                  }`} />
                </div>
              </div>
              {kpi.trend !== undefined && (
                <div className={`flex items-center gap-1 mt-3 text-sm ${kpi.trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {kpi.trend >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                  <span>vs mes anterior</span>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Access Buttons for Pasajeros */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card 
          className="shadow-sm cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-yellow-500"
          onClick={() => navigate('/pasajeros/pendientes')}
          data-testid="pending-passengers-card"
        >
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-yellow-100 rounded-lg">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Pasajeros Pendientes</p>
                  <p className="text-3xl font-bold text-yellow-600">{pasajerosStats.pending}</p>
                </div>
              </div>
              <ArrowUpRight className="w-5 h-5 text-slate-400" />
            </div>
            <p className="text-xs text-slate-500 mt-3">Click para ver detalle por gestor</p>
          </CardContent>
        </Card>

        <Card 
          className="shadow-sm cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-green-500"
          onClick={() => navigate('/pasajeros/completados')}
          data-testid="completed-passengers-card"
        >
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Pasajeros Completados</p>
                  <p className="text-3xl font-bold text-green-600">{pasajerosStats.completed}</p>
                </div>
              </div>
              <ArrowUpRight className="w-5 h-5 text-slate-400" />
            </div>
            <p className="text-xs text-slate-500 mt-3">Click para ver detalle por gestor</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Chart */}
        <Card className="lg:col-span-2 shadow-sm" data-testid="sales-chart">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-900">
              Tendencia de Ventas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#64748B" 
                    fontSize={12}
                    tickFormatter={(value) => value.slice(5)}
                  />
                  <YAxis 
                    stroke="#64748B" 
                    fontSize={12}
                    tickFormatter={(value) => `$${(value/1000).toFixed(0)}k`}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Line 
                    type="monotone" 
                    dataKey="sales" 
                    stroke="#0044CC" 
                    strokeWidth={3}
                    dot={false}
                    activeDot={{ r: 6, fill: '#0044CC' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Top Performers */}
        <Card className="shadow-sm" data-testid="top-performers">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-500" />
              Top Gestores
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {leaderboard.length === 0 ? (
                <p className="text-slate-500 text-center py-8">Sin datos disponibles</p>
              ) : (
                leaderboard.map((entry, index) => (
                  <div 
                    key={entry.gestor_id} 
                    className={`flex items-center gap-3 p-3 rounded-lg ${
                      index === 0 ? 'podium-gold border-2' : 
                      index === 1 ? 'podium-silver border-2' : 
                      index === 2 ? 'podium-bronze border-2' : 'bg-slate-50'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                      index === 0 ? 'bg-yellow-400 text-yellow-900' :
                      index === 1 ? 'bg-slate-300 text-slate-700' :
                      index === 2 ? 'bg-amber-600 text-white' :
                      'bg-slate-200 text-slate-600'
                    }`}>
                      {entry.rank}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate">{entry.gestor_name}</p>
                      <p className="text-xs text-slate-500">{entry.total_tickets} pasajes</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-slate-900">{formatCurrency(entry.total_sales)}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tickets Chart */}
      <Card className="shadow-sm" data-testid="tickets-chart">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-slate-900">
            Pasajes Vendidos por Día
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis 
                  dataKey="date" 
                  stroke="#64748B" 
                  fontSize={12}
                  tickFormatter={(value) => value.slice(5)}
                />
                <YAxis stroke="#64748B" fontSize={12} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="tickets" fill="#FFCC00" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;
